/* =============================================================================
   Furniselan — shared core
   ============================================================================= */
(function () {
  'use strict';

  var T = window.theme || { routes: {}, strings: {} };
  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function qs(sel, ctx) { return (ctx || document).querySelector(sel); }
  function qsa(sel, ctx) { return Array.prototype.slice.call((ctx || document).querySelectorAll(sel)); }

  /** Mark an element as initialised for a module; returns false if already done. */
  function once(el, key) {
    if (!el) return false;
    var k = 'fsInit' + key;
    if (el.dataset[k]) return false;
    el.dataset[k] = '1';
    return true;
  }

  /** Format cents using the shop money format (₹ handled by Shopify format string). */
  function formatMoney(cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    var value = '';
    var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    var formatString = format || T.moneyFormat || '₹{{amount_no_decimals}}';
    function withDelims(number, precision, thousands, decimal) {
      precision = typeof precision === 'undefined' ? 2 : precision;
      thousands = thousands || ',';
      decimal = decimal || '.';
      if (isNaN(number) || number == null) return 0;
      number = (number / 100.0).toFixed(precision);
      var parts = number.split('.');
      var dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      var c = parts[1] ? decimal + parts[1] : '';
      return dollars + c;
    }
    var m = formatString.match(placeholderRegex);
    switch (m ? m[1] : 'amount') {
      case 'amount': value = withDelims(cents, 2); break;
      case 'amount_no_decimals': value = withDelims(cents, 0); break;
      case 'amount_with_comma_separator': value = withDelims(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = withDelims(cents, 0, '.', ','); break;
      case 'amount_with_space_separator': value = withDelims(cents, 2, ' ', ','); break;
      case 'amount_no_decimals_with_space_separator': value = withDelims(cents, 0, ' ', ','); break;
      default: value = withDelims(cents, 2);
    }
    return formatString.replace(placeholderRegex, value);
  }

  /**
   * Load an external script exactly once. Subsequent calls with the same src
   * return the same promise, so a section re-render never re-injects it.
   */
  var scriptRegistry = (window.__fsScripts = window.__fsScripts || {});
  function loadScriptOnce(src, attrs) {
    if (!src) return Promise.reject(new Error('No script src'));
    if (scriptRegistry[src]) return scriptRegistry[src];
    var existing = qs('script[src="' + src + '"]');
    scriptRegistry[src] = new Promise(function (resolve, reject) {
      if (existing) { resolve(existing); return; }
      var s = document.createElement('script');
      s.src = src; s.async = true; s.defer = true;
      if (attrs) Object.keys(attrs).forEach(function (k) { s.setAttribute(k, attrs[k]); });
      s.onload = function () { resolve(s); };
      s.onerror = function () { delete scriptRegistry[src]; reject(new Error('Failed to load ' + src)); };
      document.head.appendChild(s);
    });
    return scriptRegistry[src];
  }

  /** Overlay manager: one scrim shared by the cart drawer, mobile menu, search,
   *  quick view and pin modal. */
  var Overlay = {
    scrim: null,
    open: [],
    lockY: 0,
    init: function () { this.scrim = qs('[data-scrim]'); if (this.scrim && once(this.scrim, 'Scrim')) { this.scrim.addEventListener('click', function () { Overlay.closeAll(); }); } },
    /**
     * Locks page scroll with `position:fixed` on <body> (offset by the current
     * scroll position) rather than plain `overflow:hidden`. The plain version is
     * the usual culprit behind a sticky header "jumping" right after a drawer or
     * modal closes on iOS Safari — it doesn't reliably recompute sticky
     * positioning once overflow is restored. Fixed-positioning the body sidesteps
     * that entirely, and scroll is restored to the exact same spot on unlock.
     */
    lockScroll: function () {
      this.lockY = window.scrollY || window.pageYOffset || 0;
      document.body.style.top = -this.lockY + 'px';
      document.body.classList.add('is-locked');
    },
    unlockScroll: function () {
      document.body.classList.remove('is-locked');
      document.body.style.top = '';
      window.scrollTo(0, this.lockY);
    },
    show: function (el) {
      if (!el) return;
      el.classList.add('is-open');
      el.hidden = false;
      el.removeAttribute('inert');
      if (this.open.indexOf(el) < 0) this.open.push(el);
      if (this.scrim) this.scrim.classList.add('is-active');
      if (this.open.length === 1) this.lockScroll();
      var focusable = el.querySelector('input, button, a');
      if (focusable) setTimeout(function () { focusable.focus(); }, 60);
    },
    hide: function (el) {
      if (!el) return;
      el.classList.remove('is-open');
      el.setAttribute('inert', '');
      if (el.dataset.overlayHidden === 'true') el.hidden = true;
      this.open = this.open.filter(function (x) { return x !== el; });
      if (!this.open.length) { if (this.scrim) this.scrim.classList.remove('is-active'); this.unlockScroll(); }
    },
    closeAll: function () { this.open.slice().forEach(function (el) { Overlay.hide(el); }); }
  };

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') { Overlay.closeAll(); document.dispatchEvent(new CustomEvent('fs:escape')); }
  });

  /* ---------------------------------------------------------------------------
     Sticky header height — exposed as --header-height so CSS scroll-margin
     (native #hash jumps on page load) clears the header too.
     ------------------------------------------------------------------------ */
  function headerOffset() {
    var h = qs('[data-site-header]');
    if (!h) return 0;
    var pos = getComputedStyle(h).position;
    return pos === 'sticky' || pos === 'fixed' ? h.getBoundingClientRect().height : 0;
  }

  var headerObserver = null;
  function trackHeaderHeight() {
    var h = qs('[data-site-header]');
    if (!h) return;
    function set() { document.documentElement.style.setProperty('--header-height', headerOffset() + 'px'); }
    set();
    if ('ResizeObserver' in window) {
      if (headerObserver) headerObserver.disconnect();
      headerObserver = new ResizeObserver(set);
      headerObserver.observe(h);
    } else if (once(document.documentElement, 'HeaderHeight')) {
      window.addEventListener('resize', set);
    }
  }

  /* ---------------------------------------------------------------------------
     Smooth scroll to an in-page target, landing just below the sticky header.
     JS-driven on purpose — don't add `html { scroll-behavior: smooth }`:
     Overlay.unlockScroll restores the page position with window.scrollTo and
     that has to stay instant.
     ------------------------------------------------------------------------ */
  var scrolling = null;
  var STOP_EVENTS = ['wheel', 'touchstart', 'keydown', 'mousedown'];

  function stopScroll() {
    if (!scrolling) return;
    cancelAnimationFrame(scrolling.raf);
    clearTimeout(scrolling.timer);
    STOP_EVENTS.forEach(function (ev) { window.removeEventListener(ev, stopScroll, { passive: true }); });
    scrolling = null;
  }

  function scrollTopFor(el) {
    var margin = parseFloat(getComputedStyle(el).scrollMarginTop) || 0;
    var y = el.getBoundingClientRect().top + window.pageYOffset - Math.max(margin, headerOffset() + 16);
    var max = document.documentElement.scrollHeight - window.innerHeight;
    return Math.max(0, Math.min(y, max));
  }

  function easeInOutCubic(t) { return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2; }

  function focusTarget(el) {
    if (!el.matches('a[href], button, input, select, textarea, summary, [tabindex]')) el.setAttribute('tabindex', '-1');
    try { el.focus({ preventScroll: true }); } catch (e) { /* ignore */ }
  }

  /** opts: { focus: bool, onDone: fn } — onDone is skipped if the shopper scrolls mid-way. */
  function scrollToTarget(el, opts) {
    if (!el) return;
    opts = opts || {};
    stopScroll();
    var from = window.pageYOffset;
    var distance = Math.abs(scrollTopFor(el) - from);

    function finish() {
      stopScroll();
      if (opts.focus) focusTarget(el);
      if (opts.onDone) opts.onDone();
    }
    if (reduceMotion || distance < 2) { window.scrollTo(0, scrollTopFor(el)); finish(); return; }

    var duration = Math.min(1100, Math.max(450, 300 + distance * 0.25));
    var start = null;
    scrolling = { raf: 0, timer: 0 };
    STOP_EVENTS.forEach(function (ev) { window.addEventListener(ev, stopScroll, { passive: true }); });
    function step(now) {
      if (!scrolling) return;
      if (start === null) start = now;
      var p = Math.min(1, (now - start) / duration);
      // The destination is re-read every frame so late layout shifts (lazy images) can't make it land short.
      window.scrollTo(0, from + (scrollTopFor(el) - from) * easeInOutCubic(p));
      if (p < 1) scrolling.raf = requestAnimationFrame(step);
      else finish();
    }
    scrolling.raf = requestAnimationFrame(step);
    // Background tabs don't run rAF — make sure we still arrive.
    scrolling.timer = setTimeout(function () { if (scrolling) { window.scrollTo(0, scrollTopFor(el)); finish(); } }, duration + 500);
  }

  function hashTarget(hash) {
    var id = (hash || '').slice(1);
    try { id = decodeURIComponent(id); } catch (e) { /* keep raw */ }
    if (!id) return null;
    return document.getElementById(id) || document.getElementsByName(id)[0] || null;
  }

  // Any link to "#id" on the current page scrolls smoothly instead of jumping. Links whose own
  // handler already called preventDefault (e.g. [data-prefill]) are left alone, as are hashes with
  // no matching element (app hooks like WishlistHero's #hero-wishlist).
  var NO_SMOOTH = '[data-no-smooth-scroll], [data-pb-wishlist], [role="tab"]';
  document.addEventListener('click', function (e) {
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    var a = e.target.closest && e.target.closest('a[href*="#"]');
    if (!a || !a.hash || (a.target && a.target !== '_self') || a.hasAttribute('download') || a.matches(NO_SMOOTH)) return;
    if (a.origin !== location.origin || a.pathname !== location.pathname || (a.search && a.search !== location.search)) return;
    var target = hashTarget(a.hash);
    if (!target) return;
    e.preventDefault();
    // A link inside the mobile menu or a drawer: close it first so the page can scroll again.
    if (Overlay.open.length) Overlay.closeAll();
    if (location.hash !== a.hash && window.history && history.pushState) history.pushState(null, '', a.hash);
    scrollToTarget(target, { focus: true });
  });

  /* ---------------------------------------------------------------------------
     Accordion — animated <details>. Native <details> snaps open/closed; this
     animates the height between the summary and the full content and fades the
     content in/out. Applies to FAQs, job openings, SEO "read more", mobile-menu
     sub-levels and account addresses; opt anything else in with
     `<details data-accordion>`. While closing, `.is-closing` is on the
     <details> so the +/− icon flips back immediately.
     ------------------------------------------------------------------------ */
  var ACCORDION = '[data-accordion], [data-mobile-submenu], .faq-list details, details.job, .seo-content details';
  var canAnimate = typeof Element !== 'undefined' && 'animate' in Element.prototype;

  function accParts(d) {
    return Array.prototype.filter.call(d.children, function (c) { return c.tagName !== 'SUMMARY'; });
  }

  function accStop(d) {
    if (!d._fsAcc) return;
    d._fsAcc.forEach(function (anim) { anim.onfinish = null; anim.cancel(); });
    d._fsAcc = null;
  }

  // Natural height with `open` set to the given state. Toggling and restoring in the same task
  // never paints, so there's no flash.
  function accHeight(d, open) {
    var was = d.open;
    d.open = open;
    var h = d.getBoundingClientRect().height;
    d.open = was;
    return h;
  }

  function accSet(d, open) {
    if (!canAnimate || reduceMotion) { accStop(d); d.classList.remove('is-closing'); d.style.overflow = ''; d.open = open; return; }
    var parts = accParts(d);
    var animating = !!d._fsAcc;
    // Start from wherever it is right now, so a second click mid-animation reverses smoothly.
    var from = d.getBoundingClientRect().height;
    var fromOpacity = parts.map(function (el) { return animating ? getComputedStyle(el).opacity : (open ? '0' : '1'); });
    accStop(d);

    // Clip during the animation, and measure while clipped so child margins count the same at both ends.
    d.style.overflow = 'hidden';
    var to = accHeight(d, open);
    d.open = true;
    d.classList.toggle('is-closing', !open);

    // Longer for taller content, but never snappy: ~420ms for a one-line answer, up to 700ms.
    var duration = Math.round(Math.min(700, Math.max(420, 300 + Math.abs(to - from) * 0.9)));
    var height = d.animate({ height: [from + 'px', to + 'px'] }, { duration: duration, easing: 'cubic-bezier(.45, 0, .25, 1)', fill: 'forwards' });
    var anims = [height];
    parts.forEach(function (el, i) {
      anims.push(el.animate(
        open ? { opacity: [fromOpacity[i], 1], transform: ['translateY(-.375rem)', 'none'] } : { opacity: [fromOpacity[i], 0] },
        { duration: open ? duration : Math.round(duration * 0.6), easing: 'ease', fill: 'forwards' }
      ));
    });
    d._fsAcc = anims;

    height.onfinish = function () {
      if (!open) d.open = false;
      d.classList.remove('is-closing');
      d.style.overflow = '';
      accStop(d);
    };
  }

  function accIsOpen(d) { return d.open && !d.classList.contains('is-closing'); }

  var Accordion = {
    selector: ACCORDION,
    open: function (d) { if (d && !accIsOpen(d)) accSet(d, true); },
    close: function (d) { if (d && accIsOpen(d)) accSet(d, false); },
    toggle: function (d) { if (d) accSet(d, !accIsOpen(d)); }
  };

  document.addEventListener('click', function (e) {
    if (e.defaultPrevented) return;
    var summary = e.target.closest && e.target.closest('summary');
    if (!summary) return;
    var d = summary.parentElement;
    if (!d || d.tagName !== 'DETAILS' || !d.matches(ACCORDION)) return;
    // Links/buttons inside a summary keep their own behaviour (e.g. the category link in a mobile-menu row).
    var control = e.target.closest('a, button, input, select, textarea, label');
    if (control && control !== summary && summary.contains(control)) return;
    e.preventDefault();
    Accordion.toggle(d);
  });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', trackHeaderHeight);
  else trackHeaderHeight();
  document.addEventListener('shopify:section:load', trackHeaderHeight);

  window.FS = {
    qs: qs, qsa: qsa, once: once, T: T, reduceMotion: reduceMotion, formatMoney: formatMoney, loadScriptOnce: loadScriptOnce, Overlay: Overlay,
    Accordion: Accordion, scrollToTarget: scrollToTarget, headerOffset: headerOffset
  };
})();
